import { useMemo } from 'react'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import Loading from '@/components/Loading'
import {
  useOwnerBillingOverviewQuery,
  useOwnerMessageCreditsQuery,
  useOwnerMessagingSubscriptionPaymentsQuery,
  useOwnerSubscriptionPaymentsQuery,
} from '@/hooks/useOwnerBilling'

function bytesToHuman(s: string) {
  const n = Number(s)
  if (!Number.isFinite(n)) return s
  const kb = 1024
  const mb = kb * 1024
  const gb = mb * 1024
  if (n >= gb) return `${(n / gb).toFixed(2)} GB`
  if (n >= mb) return `${(n / mb).toFixed(2)} MB`
  if (n >= kb) return `${(n / kb).toFixed(2)} KB`
  return `${n} B`
}

function money(n?: number | null, currency = 'XOF') {
  if (n == null || !Number.isFinite(n)) return '—'
  return `${new Intl.NumberFormat('fr-FR').format(n)} ${currency}`
}

function dateText(value?: string | null) {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('fr-FR')
}

function shortDate(value?: string | null) {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('fr-FR')
}

function badgeVariant(status?: string) {
  if (status === 'ACTIVE' || status === 'TRIAL') return 'default'
  if (status === 'EXPIRED' || status === 'NOT_PAID') return 'secondary'
  return 'destructive'
}

function tone(status?: string) {
  if (status === 'ACTIVE' || status === 'TRIAL') {
    return 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-300'
  }
  if (status === 'EXPIRED' || status === 'NOT_PAID') {
    return 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-300'
  }
  return 'border-red-300 bg-red-50 text-red-900 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300'
}

export default function OwnerBillingCard() {
  const overview = useOwnerBillingOverviewQuery(true)
  const subs = useOwnerSubscriptionPaymentsQuery(true, 10)
  const msgSubs = useOwnerMessagingSubscriptionPaymentsQuery(true, 10)
  const credits = useOwnerMessageCreditsQuery(true, 10)

  const pending =
    overview.status === 'pending' ||
    subs.status === 'pending' ||
    msgSubs.status === 'pending' ||
    credits.status === 'pending'

  const refreshAll = async () => {
    await Promise.all([
      overview.refetch(),
      subs.refetch(),
      msgSubs.refetch(),
      credits.refetch(),
    ])
  }

  const headerBadges = useMemo(() => {
    const data = overview.data
    if (!data?.ok) return null

    return (
      <div className='flex flex-wrap items-center gap-2'>
        <Badge variant={badgeVariant(data.plan.status)}>
          Plan {data.plan.status}
        </Badge>
        <Badge variant={badgeVariant(data.messaging.status)}>
          Messaging {data.messaging.status}
        </Badge>
        <Badge variant={data.account.isActive ? 'default' : 'destructive'}>
          {data.account.isActive ? 'Compte actif' : 'Compte inactif'}
        </Badge>
      </div>
    )
  }, [overview.data])

  if (pending) return <Loading />

  if (!overview.data?.ok) {
    return (
      <Card>
        <CardHeader>
          <h2 className='text-xl font-semibold'>Billing</h2>
        </CardHeader>
        <CardContent className='text-sm text-destructive'>
          Impossible de charger les informations de billing.
        </CardContent>
      </Card>
    )
  }

  const data = overview.data
  const mainPayments = subs.data?.items ?? []
  const messagingPayments = msgSubs.data?.items ?? []
  const creditRows = credits.data?.items ?? []

  return (
    <Card>
      <CardHeader>
        <div className='flex items-start justify-between gap-3'>
          <div>
            <h3 className='text-lg font-semibold'>Billing</h3>
            <p className='text-xs text-muted-foreground'>
              Plan, messaging, quotas et derniers mouvements.
            </p>
          </div>

          <div className='flex flex-col items-end gap-2'>
            {headerBadges}
            <Button
              variant='ghost'
              size='sm'
              onClick={refreshAll}
              disabled={
                overview.isFetching ||
                subs.isFetching ||
                msgSubs.isFetching ||
                credits.isFetching
              }
            >
              Actualiser
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className='space-y-4'>
        <div className='grid gap-4 md:grid-cols-3'>
          <div className='rounded-md border p-3'>
            <div className='flex items-center justify-between mb-2'>
              <div className='text-sm font-medium'>Abonnement principal</div>
              <span
                className={
                  'text-xs rounded-full border px-2 py-0.5 ' +
                  tone(data.plan.status)
                }
              >
                {data.plan.status}
              </span>
            </div>

            <div className='space-y-1 text-sm'>
              <div className='flex justify-between gap-3'>
                <span className='text-muted-foreground'>Plan</span>
                <span>{data.plan.code}</span>
              </div>
              <div className='flex justify-between gap-3'>
                <span className='text-muted-foreground'>Début</span>
                <span>{shortDate(data.plan.startedAt)}</span>
              </div>
              <div className='flex justify-between gap-3'>
                <span className='text-muted-foreground'>Échéance</span>
                <span>{shortDate(data.plan.nextRenewalAt)}</span>
              </div>
            </div>
          </div>

          <div className='rounded-md border p-3'>
            <div className='flex items-center justify-between mb-2'>
              <div className='text-sm font-medium'>Messaging</div>
              <span
                className={
                  'text-xs rounded-full border px-2 py-0.5 ' +
                  tone(data.messaging.status)
                }
              >
                {data.messaging.status}
              </span>
            </div>

            <div className='space-y-1 text-sm'>
              <div className='flex justify-between gap-3'>
                <span className='text-muted-foreground'>Plan</span>
                <span>{data.messaging.plan ?? '—'}</span>
              </div>
              <div className='flex justify-between gap-3'>
                <span className='text-muted-foreground'>Début</span>
                <span>{shortDate(data.messaging.startedAt)}</span>
              </div>
              <div className='flex justify-between gap-3'>
                <span className='text-muted-foreground'>Échéance</span>
                <span>{shortDate(data.messaging.nextRenewalAt)}</span>
              </div>
            </div>
          </div>

          <div className='rounded-md border p-3'>
            <div className='text-sm font-medium mb-2'>Compte & stockage</div>

            <div className='space-y-1 text-sm'>
              <div className='flex justify-between gap-3'>
                <span className='text-muted-foreground'>Compte</span>
                <span>
                  {data.account.isActive ? 'Actif' : 'Inactif'}
                  {data.account.suspendedAt ? ' / Suspendu' : ''}
                </span>
              </div>
              <div className='flex justify-between gap-3'>
                <span className='text-muted-foreground'>Stockage utilisé</span>
                <span>{bytesToHuman(data.storage.used)}</span>
              </div>
              <div className='flex justify-between gap-3'>
                <span className='text-muted-foreground'>Stockage max</span>
                <span>{bytesToHuman(data.storage.max)}</span>
              </div>
              <div className='flex justify-between gap-3'>
                <span className='text-muted-foreground'>Période</span>
                <span>{data.messages.periodKey}</span>
              </div>
            </div>
          </div>
        </div>

        <div className='rounded-md border p-3'>
          <div className='text-sm font-medium mb-3'>Quotas messages</div>

          <div className='grid gap-3 md:grid-cols-3'>
            {(['EMAIL', 'SMS', 'WHATSAPP'] as const).map((ch) => {
              const row = data.messages.byChannel[ch]
              return (
                <div key={ch} className='rounded-md border p-3'>
                  <div className='flex items-center justify-between mb-2'>
                    <div className='font-medium text-sm'>{ch}</div>
                    <Badge variant='secondary'>{row.remaining} restants</Badge>
                  </div>

                  <div className='space-y-1 text-xs text-muted-foreground'>
                    <div className='flex justify-between gap-3'>
                      <span>Inclus plan</span>
                      <span>{row.planIncluded}</span>
                    </div>
                    <div className='flex justify-between gap-3'>
                      <span>Topup</span>
                      <span>{row.topupIncluded}</span>
                    </div>
                    <div className='flex justify-between gap-3'>
                      <span>Total</span>
                      <span>{row.totalIncluded}</span>
                    </div>
                    <div className='flex justify-between gap-3'>
                      <span>Utilisés</span>
                      <span>{row.used}</span>
                    </div>
                    <div className='flex justify-between gap-3'>
                      <span>Overage</span>
                      <span>{row.overageCfa} CFA</span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <div className='rounded-md border p-3'>
          <div className='flex items-center justify-between mb-2'>
            <div className='text-sm font-medium'>Derniers paiements</div>
          </div>

          <div className='max-h-72 overflow-auto text-sm'>
            {mainPayments.length === 0 && messagingPayments.length === 0 ? (
              <div className='text-muted-foreground'>
                Aucun paiement récent.
              </div>
            ) : (
              <>
                {mainPayments.map((row) => (
                  <div
                    key={`main-${row.id}`}
                    className='py-2 border-b last:border-b-0'
                  >
                    <div className='flex items-center justify-between gap-3'>
                      <div>
                        <div className='font-medium'>
                          Abonnement principal • {row.plan}
                        </div>
                        <div className='text-xs text-muted-foreground'>
                          {dateText(row.createdAt)}
                        </div>
                      </div>
                      <span className='text-xs rounded-full border px-2 py-0.5'>
                        {row.status}
                      </span>
                    </div>

                    <div className='mt-1 text-xs text-muted-foreground flex items-center justify-between gap-3'>
                      <span>{money(row.amountCfa, row.currency)}</span>
                      <span>
                        Wave: {row.wavePaymentId ?? row.waveSessionId ?? '—'}
                      </span>
                    </div>

                    {row.errorMessage ? (
                      <div className='mt-1 text-xs text-red-600'>
                        {row.errorMessage}
                      </div>
                    ) : null}
                  </div>
                ))}

                {messagingPayments.map((row) => (
                  <div
                    key={`msg-${row.id}`}
                    className='py-2 border-b last:border-b-0'
                  >
                    <div className='flex items-center justify-between gap-3'>
                      <div>
                        <div className='font-medium'>
                          Abonnement messaging • {row.plan}
                        </div>
                        <div className='text-xs text-muted-foreground'>
                          {dateText(row.createdAt)}
                        </div>
                      </div>
                      <span className='text-xs rounded-full border px-2 py-0.5'>
                        {row.status}
                      </span>
                    </div>

                    <div className='mt-1 text-xs text-muted-foreground flex items-center justify-between gap-3'>
                      <span>{money(row.amountCfa, row.currency)}</span>
                      <span>
                        Wave: {row.wavePaymentId ?? row.waveSessionId ?? '—'}
                      </span>
                    </div>

                    {row.errorMessage ? (
                      <div className='mt-1 text-xs text-red-600'>
                        {row.errorMessage}
                      </div>
                    ) : null}
                  </div>
                ))}
              </>
            )}
          </div>
        </div>

        <div className='rounded-md border p-3'>
          <div className='flex items-center justify-between mb-2'>
            <div className='text-sm font-medium'>Derniers crédits messages</div>
          </div>

          <div className='max-h-72 overflow-auto text-sm'>
            {creditRows.length === 0 ? (
              <div className='text-muted-foreground'>
                Aucun crédit de messages.
              </div>
            ) : (
              creditRows.map((row) => (
                <div key={row.id} className='py-2 border-b last:border-b-0'>
                  <div className='flex items-center justify-between gap-3'>
                    <div>
                      <div className='font-medium'>
                        {row.channel} • +{row.qty}
                      </div>
                      <div className='text-xs text-muted-foreground'>
                        {dateText(row.createdAt)}
                      </div>
                    </div>
                    <span className='text-xs rounded-full border px-2 py-0.5'>
                      {row.source}
                    </span>
                  </div>

                  <div className='mt-1 text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1'>
                    <span>Période: {row.periodKey}</span>
                    <span>
                      Montant: {money(row.amountCfa, row.currency ?? 'XOF')}
                    </span>
                    <span>
                      Pack:{' '}
                      {row.pack ? `${row.pack.name} (${row.pack.code})` : '—'}
                    </span>
                  </div>

                  {row.note ? (
                    <div className='mt-1 text-xs text-muted-foreground'>
                      Note: {row.note}
                    </div>
                  ) : null}
                </div>
              ))
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
